import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import api from '../../services/api';

interface Notice {
  id: number;
  title: string;
  content: string;
  type: string;
  created_at: string;
}

interface Bill {
  id: number;
  amount: number;
  status: string;
  due_date: string;
  paid_date?: string;
}

interface Complaint {
  id: number;
  subject: string;
  description: string;
  status: string;
  created_at: string;
}

const ResidentDashboard: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [loading, setLoading] = useState(true);
  const user = authService.getCurrentUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [noticesRes, billsRes, complaintsRes] = await Promise.all([
          api.get('/notices'),
          api.get(`/bills/user/${user?.id}`),
          api.get(`/complaints/user/${user?.id}`)
        ]);
        
        // Ensure data is an array before setting state
        setNotices(Array.isArray(noticesRes.data) ? noticesRes.data : []);
        setBills(Array.isArray(billsRes.data) ? billsRes.data : []);
        setComplaints(Array.isArray(complaintsRes.data) ? complaintsRes.data : []);
      } catch (error) {
        console.error('Error fetching data:', error);
        // Set empty arrays on error
        setNotices([]);
        setBills([]);
        setComplaints([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="resident-dashboard">
      <header className="dashboard-header">
        <h1>Resident Dashboard</h1>
        <p>Welcome, {user?.name} (Flat: {user?.flat_no})</p>
      </header>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h3>Latest Notices</h3>
          <div className="notices-list">
            {notices && notices.length > 0 ? notices.slice(0, 5).map(notice => (
              <div key={notice.id} className="notice-item">
                <h4>{notice.title}</h4>
                <p>{notice.content}</p>
                <small>{new Date(notice.created_at).toLocaleDateString()}</small>
              </div>
            )) : (
              <p>No notices found</p>
            )}
          </div>
          <Link to="/resident/notices" className="view-all-link">View All Notices</Link>
        </div>

        <div className="dashboard-card">
          <h3>Bills & Payments</h3>
          <div className="bills-list">
            {bills && bills.length > 0 ? bills.slice(0, 3).map(bill => (
              <div key={bill.id} className="bill-item">
                <div className="bill-info">
                  <span>Amount: ₹{bill.amount}</span>
                  <span className={`status ${bill.status}`}>{bill.status}</span>
                </div>
                <small>Due: {new Date(bill.due_date).toLocaleDateString()}</small>
              </div>
            )) : (
              <p>No bills found</p>
            )}
          </div>
          <Link to="/resident/bills" className="view-all-link">View All Bills</Link>
        </div>

        <div className="dashboard-card">
          <h3>My Complaints</h3>
          <div className="complaints-list">
            {complaints && complaints.length > 0 ? complaints.slice(0, 3).map(complaint => (
              <div key={complaint.id} className="complaint-item">
                <h4>{complaint.subject}</h4>
                <p>{complaint.description}</p>
                <span className={`status ${complaint.status}`}>{complaint.status}</span>
              </div>
            )) : (
              <p>No complaints found</p>
            )}
          </div>
          <Link to="/resident/complaints" className="view-all-link">View All Complaints</Link>
        </div>

        <div className="dashboard-card">
          <h3>Quick Actions</h3>
          <div className="quick-actions">
            <Link to="/resident/complaints/new" className="action-btn">
              Submit Complaint
            </Link>
            <Link to="/resident/bills" className="action-btn">
              Pay Bills
            </Link>
            <Link to="/resident/profile" className="action-btn">
              Update Profile
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResidentDashboard;
